/* eslint-disable @typescript-eslint/camelcase */
import axios, { AxiosResponse } from 'axios';
import { createSelector } from 'reselect';
import * as R from 'ramda';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { getClientCode, getSessionKey, getUserLoggedIn } from 'reducers/Login';
import { getSetting } from 'reducers/configs/settings';
import { proxy } from 'services/shared';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import { getServiceEndpoints } from 'reducers/configs/serviceEnpoints';
import { RootState } from 'reducers';

import { getTemplateEngineApi } from './APITemplateEngine';
import {
  CustomerMembership,
  CustomerMasterRecord,
  CustomerPurchaseDate,
  CustomerStats,
  LegacyRecentSaleResponse,
} from './types';
import {
  mapLegacyRecentSales,
  mapRecentSales,
  RecentSale,
  sortSalesHistory,
} from './utils';

const getSalesBaseUrl = state =>
  getSetting('plugin_reorder_worksheet_api_endpoint')(state);

const getDscUrl = state =>
  getSetting('plugin_dsc_loyalty_membership_base_url')(state);

/**
 * @TODO on sandbox, remove false from here and move it to the defaultSettings.ts file as use_dsc_membership_service: common.bool(false),
 */
const getShouldUseDSCAPI = state =>
  !!getSetting('use_dsc_membership_service', false)(state);

const getMembershipURL = createSelector(
  getShouldUseDSCAPI,
  getServiceEndpoints,
  getDscUrl,
  (shouldUseOldAPI, servicePoints, oldAPIUrl) => {
    return String(
      (shouldUseOldAPI ? oldAPIUrl : servicePoints?.memberships?.url) ??
        'https://api-lms-us.erply.com/',
    );
  },
);

const getCustomerUrl = createSelector(
  getUserLoggedIn,
  ({ customerRegistryURLs: [url] }) => url?.url,
);

const getJWT = createSelector(
  getUserLoggedIn,
  ({ customerRegistryURLs: [url] }) => url?.token,
);

const getKeys = createSelector(
  getSalesBaseUrl,
  getDscUrl,
  getCustomerUrl,
  getClientCode,
  getSessionKey,
  getJWT,
  getMembershipURL,
  (
    salesBaseUrl,
    dscUrl,
    customerUrl,
    clientCode,
    sessionKey,
    jwt,
    membershipURL,
  ) => ({
    jwt,
    dscUrl,
    customerUrl,
    clientCode,
    sessionKey,
    salesBaseUrl,
    membershipURL,
  }),
);

const getHeaders = createSelector(
  getKeys,
  getShouldUseDSCAPI,
  ({ jwt, clientCode, sessionKey }, shouldUseOldAPI) => {
    const commonHeaders = {
      'Content-Type': 'application/json',
      'Customer-Registry-JWT': jwt,
    };
    const lmsHeaders = {
      clientCode,
      sessionKey,
    };
    const dscHeaders = {
      'Erply-Api-Client-Code': clientCode,
      'Erply-Api-Session-Key': sessionKey,
    };
    const membershipHeaders = {
      ...commonHeaders,
      ...(shouldUseOldAPI ? dscHeaders : lmsHeaders),
    };
    const baseHeaders = {
      ...commonHeaders,
      'Erply-Api-Client-Code': clientCode,
      'Erply-Api-Session-Key': sessionKey,
    };
    return {
      baseHeaders,
      membershipHeaders,
    };
  },
);

/**
 * @see https://wbu-sh-sb.erply.com/v1/documentation#/
 */
const WBUAPI = {
  getMembershipData: customerId => async (
    _dispatch,
    getState: () => RootState,
  ) => {
    const state = getState();
    const { membershipURL } = getKeys(state);
    const { membershipHeaders: headers } = getHeaders(getState());
    const url = `${membershipURL}memberships?customerId=${customerId}&page=1&recordsPerPage=25`;
    return axios
      .get(url, {
        headers,
      })
      .then(res => res.data);
  },

  // https://wbudcs-sb.erply.com/v1/customers/{customer_id}/memberships
  // DSC member since
  getCustomerMemberships: customerID => async (
    _dispatch,
    getState: () => RootState,
  ): Promise<CustomerMembership> => {
    const { membershipURL } = getKeys(getState());
    const useOldAPI = getShouldUseDSCAPI(getState());
    const url = `${
      !useOldAPI ? proxy : ''
    }${membershipURL}customers/${customerID}/memberships`;
    const { membershipHeaders: headers } = getHeaders(getState());

    return axios.get(url, { headers }).then(res => res.data);
  },

  // https://wbudcs-sb.erply.com/v1/membership-purchases
  // DSC membership first purchase date and member since date
  getMembershipPurchases: ({ customerId, createdAfter }) => async (
    _dispatch,
    getState: () => RootState,
  ): Promise<{
    pagination: {
      recordsTotal: number;
      recordsInResponse: number;
      recordsPerPage: number;
      currentPage: number;
      totalPages: number;
    };
    data: CustomerPurchaseDate[];
  }> => {
    const { membershipURL } = getKeys(getState());
    const { membershipHeaders: headers } = getHeaders(getState());

    return axios
      .get(`${membershipURL}membership-purchases`, {
        params: {
          customerId: Number(customerId),
          createdAfter,
          recordsPerPage: 500,
        },
        headers,
      })
      .then(res => res.data);
  },

  // https://wbudcs-sb.erply.com/v1/membership-purchases
  // Update DSC membership date
  updateCustomerMembershipDate: ({
    customerId,
    salesDocumentId,
    amount,
  }) => async (dispatch, getState: () => RootState): Promise<unknown> => {
    const { membershipURL } = getKeys(getState());
    const { membershipHeaders: headers } = getHeaders(getState());
    const warehouseId = getSelectedWarehouseID(getState());
    const useOldAPI = getShouldUseDSCAPI(getState());

    return axios.post(
      `${membershipURL}membership-purchases`,
      {
        warehouseId: Number(warehouseId),
        customerId: Number(customerId),
        salesDocumentId: Number(salesDocumentId),
        ...(useOldAPI
          ? {
              amount,
            }
          : {
              duration: amount,
              durationUnit: 'year',
            }),
      },
      {
        headers,
      },
    );
  },

  // https://wbudcs-sb.erply.com/v1/membership-purchases/return
  // https://api-lms-sb.erply.com/documentation/index.html#/membership-purchases/post_v2_membership_purchases_return
  // Return DSC membership purchase
  returnMembershipPurchase: ({
    validationOnly,
    customerId,
    amount,
    salesDocumentId,
    relatedDocumentId,
  }) => async (
    _dispatch,
    getState: () => RootState,
  ): Promise<AxiosResponse<unknown>> => {
    const { membershipURL } = getKeys(getState());
    if (!membershipURL) {
      throw new Error('DSC membership URL is not defined');
    }
    const { membershipHeaders: headers } = getHeaders(getState());
    const warehouseId = getSelectedWarehouseID(getState());
    const useOldAPI = getShouldUseDSCAPI(getState());
    const endpoint = useOldAPI
      ? 'membership-purchases'
      : 'v2/membership-purchases';

    return axios
      .post(
        `${membershipURL}${endpoint}/return`,
        {
          validationOnly,
          warehouseId: Number(warehouseId),
          customerId: Number(customerId),
          salesDocumentId: Number(salesDocumentId),
          [useOldAPI ? 'amount' : 'duration']: amount,
          relatedDocumentId: Number(relatedDocumentId),
        },
        {
          headers,
        },
      )
      .then(res => res.data)
      .catch(err => {
        const errMsg = err?.response?.data?.message;
        if (errMsg) {
          throw new Error(errMsg);
        }
        throw new Error(err.message);
      });
  },

  // https://wbu-sh-sb.erply.com/v1/reports/customer-stats
  // Total visits, total sales, DSC savings, life savings, last visited
  // Gets customer purchase statistics through a POST request
  // The body is the customer's ID and a date (currently just null)
  getCustomerStats: (customerID: string) => async (
    dispatch: ThunkDispatch<any, any, Action>,
    getState,
  ): Promise<{
    status?: {
      responseStatus: 'ok' | string;
      recordsTotal: number;
      generationTime: number;
    };
    data: CustomerStats[];
  }> => {
    if (Number(getSetting('WBUCustomerstatistics', '0')(getState()))) {
      return getTemplateEngineApi(getState())
        .getCustomerStatistics({
          id: Number(customerID),
        })
        .then(({ data: { data: customer } }) => ({
          data: [
            {
              customer_id: customer.customer_id,
              'max(cdm.last_transaction_id)': Number(
                customer.last_transaction_id,
              ),
              'max(cdm.last_transaction_number)': Number(
                customer.last_transaction_number,
              ),
              last_visit: customer.last_visit,
              first_visit: customer.first_visit,
              total_visits: Number(customer.total_visits),
              total_sales: Number(customer.total_sales),
              total_savings: Number(customer.total_savings),
              total_dsc_savings: Number(customer.total_dsc_savings),
              dsc_current_savings: Number(customer.dsc_current_savings),
              potential_savings: Number(customer.potential_savings),
            },
          ],
        }));
    }
    const { salesBaseUrl } = getKeys(getState());
    const { baseHeaders: headers } = getHeaders(getState());

    return axios
      .post(
        `${salesBaseUrl}/v1/reports/customer-stats`,
        JSON.stringify({ [customerID]: null }),
        {
          headers,
        },
      )
      .then(res => res.data)
      .catch(() => {
        return {
          data: [
            {
              customer_id: 0,
              'max(cdm.last_transaction_id)': 0,
              'max(cdm.last_transaction_number)': 0,
              last_visit: undefined,
              first_visit: undefined,
              total_visits: 0,
              total_sales: 0,
              total_savings: 0,
              total_dsc_savings: 0,
              dsc_current_savings: 0,
              potential_savings: 0,
            },
          ],
        };
      });
  },

  // https://wbu-cr-sb.erply.com/V1/Customer/getMasterRecord
  // Get regular customer data for WBU
  getCustomerMasterRecord: customerID => async (
    dispatch,
    getState,
  ): Promise<{
    api: {
      cache: {
        cached: boolean;
        ttl: any;
        expires: any;
      };
      generationTime: string;
      path: string;
      time: number;
      uniqueId: string;
    };
    error: {
      message: 'OK' | string;
      code: any | null;
    };
    result: CustomerMasterRecord;
  }> => {
    const keys = getKeys(getState());
    return axios
      .get(`${keys.customerUrl}V1/Customer/getMasterRecord`, {
        params: {
          'parameters[id]': customerID,
          'api[jwt]': keys.jwt,
        },
      })
      .then(res => res.data);
  },

  updateCustomerMasterRecord: (
    data: Partial<CustomerMasterRecord> & { id: number },
  ) => async (dispatch, getState) => {
    const { result: current } = await dispatch(
      WBUAPI.getCustomerMasterRecord(data.id),
    );
    const { baseHeaders: headers } = getHeaders(getState());
    const keys = getKeys(getState());
    return axios.post<void>(
      `${keys.customerUrl}V1/Customer/update`,
      {
        'api[jwt]': keys.jwt,
        parameters: R.mergeDeepLeft(data, current),
      },
      { headers },
    );
  },

  getRecentSales: ({
    customerID,
    count = 15,
    ...rest
  }: {
    customerID: string | number;
    count?: number;
  }) => async (dispatch, getState): Promise<RecentSale[]> => {
    const useOldSalesHistoryService = getSetting(
      'use_old_sales_history_service',
      '0',
    )(getState());
    if (!Number(useOldSalesHistoryService)) {
      const templateEngineApi = getTemplateEngineApi(getState());
      return templateEngineApi
        .getCustomerRecentSales({
          customerId: Number(customerID),
          perPage: count,
        })
        .then(response => sortSalesHistory(mapRecentSales(response.data.data)));
    }

    const { salesBaseUrl } = getKeys(getState());
    const { baseHeaders: headers } = getHeaders(getState());
    const data = new FormData();
    Object.entries({ ...rest, count, customerID }).forEach(
      ([key, value]: [string, any]) => {
        data.set(key, value);
      },
    );
    return axios
      .post<{
        status: {
          generationTime: number;
          recordsTotal: number;
          responseStatus: 'ok' | string;
        };
        data: Record<string, LegacyRecentSaleResponse>;
      }>(`${salesBaseUrl}/v1/reports/customer-recent-sales`, data, {
        headers,
      })
      .then(response =>
        sortSalesHistory(mapLegacyRecentSales(response.data.data)),
      );
  },
  createPreMembership: (customerId: string | number) => async (
    dispatch,
    getState: () => RootState,
  ) => {
    const { membershipURL } = getKeys(getState());
    const { membershipHeaders: headers } = getHeaders(getState());
    const useOldAPI = getShouldUseDSCAPI(getState());
    const urlString = useOldAPI
      ? 'pre-membership'
      : `pre-memberships?customerId=${customerId}&duration=30`;

    return axios
      .post(
        `${proxy}${membershipURL}${urlString}`,
        useOldAPI ? { customerId } : {},
        { headers },
      )
      .then(res => res.data);
  },
  removePreMembership: (customerId: string | number) => async (
    dispatch,
    getState: () => RootState,
  ) => {
    const { membershipURL } = getKeys(getState());
    const { membershipHeaders: headers } = getHeaders(getState());
    const useOldAPI = getShouldUseDSCAPI(getState());
    const urlParams = useOldAPI
      ? 'pre-membership'
      : `pre-memberships?customerId=${customerId}`;

    return axios.delete(`${proxy}${membershipURL}${urlParams}`, {
      headers,
      data: useOldAPI
        ? {
            customerId,
          }
        : {},
    });
  },
  renewMembership: (customerId: number, endDate: any) => async (
    dispatch,
    getState: () => RootState,
  ) => {
    const shouldUseOldAPI = getShouldUseDSCAPI(getState());
    const { membershipHeaders: headers } = getHeaders(getState());
    const { membershipURL } = getKeys(getState());
    const keyToUse = shouldUseOldAPI ? 'endDateTime' : 'endDate';
    return axios
      .patch(
        `${membershipURL}memberships?customerId=${customerId}&endDate=${endDate}T23:59:59`,
        {
          customerId,
          [keyToUse]: `${endDate}T23:59:59`,
        },
        { headers },
      )
      .then(res => res.data);
  },
};

export default WBUAPI;
